import React, { useState, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField } from "@material-ui/core";
import {
	DetectionActionBtn,
	DetectionActions,
	DetectionBadge,
	DetectionItem,
	DetectionList,
	DetectionMeta,
	DetectionRail,
	DetectionRailHeader,
	DetectionText,
} from "../styles";
import { LABELS } from "../labels";
import type { StationsTextDetection } from "../types";

interface Props {
	detections: StationsTextDetection[];
	onAdd: (detection: StationsTextDetection, meta: { screen: string; group: string; tags?: string[] }) => void;
	onIgnore: (fingerprint: string) => void;
}

type AddingState = {
	fingerprint: string;
	screen: string;
	group: string;
	tags: string;
};

type BulkState =
	| { phase: "idle" }
	| { phase: "confirm" }
	| { phase: "running"; done: number; total: number }
	| { phase: "done" };

const CHUNK_SIZE = 100;

export default function DetectionInbox({ detections, onAdd, onIgnore }: Props) {
	const [adding, setAdding] = useState<AddingState | null>(null);
	const [bulk, setBulk] = useState<BulkState>({ phase: "idle" });
	const cancelRef = useRef(false);

	const newDetections = detections.filter((d) => d.status === "new");
	const newCount = newDetections.length;

	function startAdd(detection: StationsTextDetection) {
		setAdding({
			fingerprint: detection.fingerprint,
			screen: detection.suggestedScreen,
			group: detection.suggestedGroup,
			tags: "",
		});
	}

	function commitAdd(detection: StationsTextDetection) {
		if (!adding) return;
		const tags = adding.tags
			.split(",")
			.map((t) => t.trim().toLowerCase())
			.filter(Boolean);
		onAdd(detection, { screen: adding.screen, group: adding.group, tags });
		setAdding(null);
	}

	function openBulkConfirm() {
		setBulk({ phase: "confirm" });
	}

	function closeBulkDialog() {
		if (bulk.phase === "running") return; // disallow close during run
		cancelRef.current = true;
		setBulk({ phase: "idle" });
	}

	function runBulk() {
		const snapshot = [...newDetections];
		const total = snapshot.length;
		if (total === 0) {
			setBulk({ phase: "idle" });
			return;
		}

		cancelRef.current = false;
		setBulk({ phase: "running", done: 0, total });

		let cursor = 0;

		function processChunk() {
			if (cancelRef.current) {
				setBulk({ phase: "idle" });
				return;
			}

			const end = Math.min(cursor + CHUNK_SIZE, total);
			for (let i = cursor; i < end; i++) {
				const d = snapshot[i];
				onAdd(d, { screen: d.suggestedScreen, group: d.suggestedGroup, tags: [] });
			}
			cursor = end;

			if (cursor >= total) {
				setBulk({ phase: "done" });
				// close dialog after a brief moment so user sees 100%
				setTimeout(() => setBulk({ phase: "idle" }), 600);
				return;
			}

			setBulk({ phase: "running", done: cursor, total });
			requestAnimationFrame(processChunk);
		}

		requestAnimationFrame(processChunk);
	}

	const isRunning = bulk.phase === "running";
	const isDone = bulk.phase === "done";
	const bulkOpen = bulk.phase === "confirm" || isRunning || isDone;

	return (
		<DetectionRail>
			<DetectionRailHeader>
				<span>{LABELS.detectionInboxTitle}</span>
				{newCount > 0 && <DetectionBadge>{newCount}</DetectionBadge>}
			</DetectionRailHeader>

			<DetectionList>
				{newCount === 0 && (
					<DetectionItem>
						<DetectionMeta>{LABELS.detectionNoneNew}</DetectionMeta>
					</DetectionItem>
				)}

				{newCount > 0 && (
					<DetectionItem>
						<DetectionActions>
							<DetectionActionBtn
								variant="primary"
								onClick={openBulkConfirm}>
								{LABELS.bulkAddBtn(newCount)}
							</DetectionActionBtn>
						</DetectionActions>
					</DetectionItem>
				)}

				{newDetections.map((d) => {
					const isAdding = adding?.fingerprint === d.fingerprint;
					return (
						<DetectionItem key={d.fingerprint}>
							<DetectionText>{d.text}</DetectionText>
							<DetectionMeta>
								{d.file} &nbsp;·&nbsp; {d.suggestedScreen}/{d.suggestedGroup}
							</DetectionMeta>

							{isAdding ? (
								<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
									<TextField
										size="small"
										variant="outlined"
										label={LABELS.addToRegistryScreen}
										value={adding!.screen}
										onChange={(e) =>
											setAdding((prev) => prev && { ...prev, screen: e.target.value })
										}
									/>
									<TextField
										size="small"
										variant="outlined"
										label={LABELS.addToRegistryGroup}
										value={adding!.group}
										onChange={(e) =>
											setAdding((prev) => prev && { ...prev, group: e.target.value })
										}
									/>
									<TextField
										size="small"
										variant="outlined"
										label={LABELS.addToRegistryTags}
										placeholder="e.g. cta, title"
										value={adding!.tags}
										onChange={(e) => setAdding((prev) => prev && { ...prev, tags: e.target.value })}
									/>
									<DetectionActions>
										<DetectionActionBtn
											variant="primary"
											onClick={() => commitAdd(d)}>
											{LABELS.addToRegistryConfirm}
										</DetectionActionBtn>
										<DetectionActionBtn onClick={() => setAdding(null)}>
											{LABELS.addToRegistryCancel}
										</DetectionActionBtn>
									</DetectionActions>
								</div>
							) : (
								<DetectionActions>
									<DetectionActionBtn
										variant="primary"
										onClick={() => startAdd(d)}>
										{LABELS.detectionAdd}
									</DetectionActionBtn>
									<DetectionActionBtn onClick={() => onIgnore(d.fingerprint)}>
										{LABELS.detectionIgnore}
									</DetectionActionBtn>
								</DetectionActions>
							)}
						</DetectionItem>
					);
				})}
			</DetectionList>

			<Dialog
				open={bulkOpen}
				onClose={(_e: object, reason: string) => {
					if (isRunning && reason === "backdropClick") return;
					closeBulkDialog();
				}}>
				<DialogTitle>{LABELS.bulkAddConfirmTitle}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{isRunning
							? LABELS.bulkAddProgress(
									(bulk as { phase: "running"; done: number; total: number }).done,
									(bulk as { phase: "running"; done: number; total: number }).total
							  )
							: isDone
							? LABELS.bulkAddProgress(newCount, newCount)
							: LABELS.bulkAddConfirmBody(newCount)}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<DetectionActionBtn
						onClick={closeBulkDialog}
						disabled={isRunning}>
						{LABELS.bulkAddConfirmCancel}
					</DetectionActionBtn>
					{bulk.phase === "confirm" && (
						<DetectionActionBtn
							variant="primary"
							onClick={runBulk}>
							{LABELS.bulkAddConfirmOk}
						</DetectionActionBtn>
					)}
				</DialogActions>
			</Dialog>
		</DetectionRail>
	);
}
